import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  parseJsonResponse,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { syncSampleLimitFromSections, validateSampleQuantityChange } from '@theme/sample-limit';
import { mergeLineItemProperties } from '@theme/line-item-properties';

/** @typedef {import('./utilities').TextComponent} TextComponent */

const CART_ITEM_ERROR_SELECTOR = '.cart-items__error:not(.hidden) .cart-item__error-text:not(:empty)';
const CHECKOUT_TRIGGER_SELECTOR =
  'button[name="checkout"], input[name="checkout"], .additional-checkout-buttons, shopify-accelerated-checkout-cart';
const CART_ERROR_DISABLED_ATTRIBUTE = 'data-cart-error-disabled';

const hasCartItemErrors = () => Boolean(document.querySelector(CART_ITEM_ERROR_SELECTOR));

function syncCheckoutErrorState() {
  const hasErrors = hasCartItemErrors();

  for (const control of document.querySelectorAll(CHECKOUT_TRIGGER_SELECTOR)) {
    if (!(control instanceof HTMLButtonElement || control instanceof HTMLInputElement)) continue;

    if (hasErrors && control.disabled) continue;
    if (!hasErrors && !control.hasAttribute(CART_ERROR_DISABLED_ATTRIBUTE)) continue;

    control.disabled = hasErrors;
    control.toggleAttribute(CART_ERROR_DISABLED_ATTRIBUTE, hasErrors);
    control.toggleAttribute('aria-disabled', hasErrors);
  }
}

/** @param {Event} event */
function handleCheckoutErrorGuard(event) {
  const target = event.target;
  if (!(target instanceof Element) || !hasCartItemErrors()) return;
  if (target.id !== 'cart-form' && !target.closest(CHECKOUT_TRIGGER_SELECTOR)) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  syncCheckoutErrorState();
}

document.addEventListener('submit', handleCheckoutErrorGuard, true);
document.addEventListener('click', handleCheckoutErrorGuard, true);

const waitForPaint = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** @type {boolean} */
  #didOptimisticEmpty = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * @param {HTMLElement | undefined} quantitySelector
   * @returns {HTMLInputElement | null}
   */
  #getQuantityInput(quantitySelector) {
    if (!quantitySelector) return null;

    if (
      'refs' in quantitySelector &&
      typeof quantitySelector.refs === 'object' &&
      quantitySelector.refs !== null &&
      'quantityInput' in quantitySelector.refs &&
      quantitySelector.refs.quantityInput instanceof HTMLInputElement
    ) {
      return quantitySelector.refs.quantityInput;
    }

    const quantityInput = quantitySelector.querySelector('input[name="updates[]"], input[ref="quantityInput"]');
    if (quantityInput instanceof HTMLInputElement) return quantityInput;

    const fallbackInput = quantitySelector.querySelector('input');
    return fallbackInput instanceof HTMLInputElement ? fallbackInput : null;
  }

  /**
   * @param {HTMLElement | undefined} quantitySelector
   * @param {string | number} quantity
   */
  #resetQuantitySelector(quantitySelector, quantity) {
    if (!quantitySelector) return;

    if ('resetQuantity' in quantitySelector && typeof quantitySelector.resetQuantity === 'function') {
      quantitySelector.resetQuantity(quantity);
    } else {
      const quantityInput = this.#getQuantityInput(quantitySelector);
      if (quantityInput) {
        quantityInput.value = String(quantity);
      }
    }

    if ('updateButtonStates' in quantitySelector && typeof quantitySelector.updateButtonStates === 'function') {
      quantitySelector.updateButtonStates();
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line, lineItemProperties } = event.detail;

    // Cart items require a line number
    if (!line) return;

    const eventLineItemRow = event.target instanceof Element ? event.target.closest('[ref="cartItemRows[]"]') : null;
    const lineItemRow =
      eventLineItemRow instanceof HTMLTableRowElement ? eventLineItemRow : this.refs.cartItemRows[line - 1];
    const currentQuantity = Number(lineItemRow?.dataset.sampleLimitQuantity) || 0;
    const isSample = lineItemRow?.dataset.sampleLimitSample === 'true';
    const mergedLineItemProperties = lineItemProperties
      ? mergeLineItemProperties(lineItemRow?.dataset.lineItemProperties, lineItemProperties)
      : undefined;

    if (
      !validateSampleQuantityChange({
        isSample,
        currentQuantity,
        nextQuantity: quantity,
      })
    ) {
      const quantitySelector = this.refs.quantitySelectors[line - 1];
      this.#resetQuantitySelector(quantitySelector, currentQuantity);
      return;
    }

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
      properties: mergedLineItemProperties,
    });
    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      this.#didOptimisticEmpty = true;

      const swapToEmpty = () => {
        this.#applyDrawerEmptyChrome();
        this.replaceChildren(clone);
      };

      // Document view transitions steal the top layer and hide <dialog>::backdrop.
      if (this.isDrawer) {
        this.#transitionDrawerToEmpty(swapToEmpty);
      } else {
        startViewTransition(swapToEmpty, ['empty-cart-page']);
      }

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {Record<string, string> | undefined} [config.properties] - Line item properties to sync
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity, properties } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      ...(properties ? { properties } : {}),
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => parseJsonResponse(response))
      .then((parsedResponseText) => {
        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#didOptimisticEmpty = false;
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);
        syncSampleLimitFromSections(parsedResponseText.sections);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        const skipEmptyDrawerMorph = this.#didOptimisticEmpty && this.isDrawer && newCartItemCount === 0;
        this.#didOptimisticEmpty = false;

        if (!skipEmptyDrawerMorph) {
          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }

        this.#updateCartQuantitySelectorButtonStates();
        syncCheckoutErrorState();
      })
      .catch((error) => {
        this.#didOptimisticEmpty = false;
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = this.#getQuantityInput(quantitySelector);

    if (!quantityInput) throw new Error('Quantity input not found');

    this.#resetQuantitySelector(quantitySelector, quantityInput.defaultValue);

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
    syncCheckoutErrorState();
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(syncCheckoutErrorState);
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      syncSampleLimitFromSections(event.detail.data.sections);
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      syncCheckoutErrorState();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(syncCheckoutErrorState);
    }
  };

  /**
   * Applies empty-cart layout classes on the drawer dialog so the optimistic
   * empty view matches the server-rendered empty state (without waiting for morph).
   */
  #applyDrawerEmptyChrome() {
    if (!this.isDrawer) return;

    const dialog = this.closest('dialog');
    if (!dialog) return;

    dialog.classList.add('cart-drawer--empty');
    dialog.setAttribute('aria-labelledby', 'cart-drawer-heading-empty');
    dialog.setAttribute('cart-summary-sticky', 'false');
  }

  /**
   * Crossfades filled drawer content to the empty template without a document view
   * transition (those hide <dialog>::backdrop).
   * @param {() => void} swap
   */
  async #transitionDrawerToEmpty(swap) {
    if (prefersReducedMotion()) {
      swap();
      return;
    }

    const contentToAnimate = () =>
      [...this.children].filter((child) => !child.classList.contains('cart-drawer__header'));

    this.classList.add('cart-items-component--emptying');
    await waitForPaint();
    await onAnimationEnd(contentToAnimate(), undefined, { subtree: false });

    swap();

    this.classList.remove('cart-items-component--emptying');
    this.classList.add('cart-items-component--empty-enter');
    await waitForPaint();
    await onAnimationEnd(contentToAnimate(), undefined, { subtree: false });
    this.classList.remove('cart-items-component--empty-enter');
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
